import { ErrorBanner, Loading } from '../../components/ui';
import { api } from '../../lib/api';
import { num, pct } from '../../lib/format';
import { useHeldResource } from '../../lib/hooks';

/**
 * One client's agent health, live from ConnectWise Automate — the client's
 * own Agent health tab and the review deck's slide read the same thing from
 * the same endpoint (GET /am/clients/:id/automate/agent-health,
 * routes/amAutomate.ts), judged by the same rules as NOC › Estate health.
 *
 * NOT LINKED IS NOT ZERO. A client whose Automate client has not been mapped
 * (migration 185) answers `linked: false`, and both views say so in words. A
 * zero here would read as "we monitor nothing of yours", which in front of a
 * client is a worse mistake than an empty panel.
 */

export interface Agent {
  id: number;
  name: string;
  client: string;
  automateClientId: number;
  clientId: number | null;
  location: string;
  type: string;
  os: string;
  online: boolean;
  lastContact: string | null;
  offlineDays: number | null;
  agentVersion: string;
  patchAgeDays: number | null;
  virusScanner: string;
  avAgeDays: number | null;
  rebootNeeded: boolean;
  maintenance: boolean;
  flags: string[];
}

/** What the slide keeps on the review. */
export interface AgentHealthSnap {
  readAt: string;
  linked: boolean;
  automateClientIds: number[];
  thresholds: { offlineDays: number; patchDays: number; avDays: number; seenDays: number };
  totals: {
    agents: number;
    online: number;
    offline: number;
    servers: number;
    workstations: number;
    maintenance: number;
    offlineTooLong: number;
    patchJudged: number;
    patchStale: number;
    patchUnknown: number;
    avStale: number;
    avMissing: number;
    rebootNeeded: number;
    healthy: number;
  };
  attention: Agent[];
  attentionTotal: number;
}

export async function loadAgentHealth(clientId: string, refresh: boolean): Promise<AgentHealthSnap> {
  return api.get<AgentHealthSnap>(`/am/clients/${clientId}/automate/agent-health${refresh ? '?refresh=1' : ''}`);
}

const share = (part: number, whole: number) => (whole ? pct((part / whole) * 100, 0) : '—');
export const agentDays = (n: number | null) => (n === null ? '—' : n === 0 ? 'today' : `${num(n)}d`);

/**
 * The four figures a client actually asks about: how many machines we watch,
 * how many are talking to us, how many are patched, and how many are clean.
 *
 * Stated as counts with the share underneath rather than as percentages
 * alone — "3 of 48" is a conversation, "94%" is a number to nod at.
 */
export function AgentHealthFigures({ data }: { data: AgentHealthSnap }) {
  const t = data.totals;
  return (
    <div className="am-ah-figures">
      <Figure label="Agents monitored" value={num(t.agents)} foot={`${num(t.servers)} servers · ${num(t.workstations)} workstations`} />
      <Figure
        label="Checking in"
        value={share(t.online, t.agents)}
        foot={t.offlineTooLong > 0 ? `${num(t.offlineTooLong)} offline over ${data.thresholds.offlineDays} days` : 'all seen recently'}
        warn={t.offlineTooLong > 0}
      />
      <Figure
        label="Up to date on patches"
        value={share(t.patchJudged - t.patchStale - t.patchUnknown, t.patchJudged)}
        foot={`${num(t.patchStale)} over ${data.thresholds.patchDays} days · of ${num(t.patchJudged)} Windows agents`}
        warn={t.patchStale > 0}
      />
      <Figure
        label="Nothing outstanding"
        value={share(t.healthy, t.agents)}
        foot={`${num(t.agents - t.healthy)} need something`}
        warn={t.agents - t.healthy > 0}
      />
    </div>
  );
}

function Figure({ label, value, foot, warn }: { label: string; value: string; foot: string; warn?: boolean }) {
  return (
    <div className="am-ah-figure">
      <span className="am-ah-label">{label}</span>
      <span className={`am-ah-value${warn ? ' is-warn' : ''}`}>{value}</span>
      <span className="am-ah-foot">{foot}</span>
    </div>
  );
}

/**
 * The machines that need something, worst first.
 *
 * `limit` is what makes this usable on a slide: a review shows the worst
 * handful and says how many more there are, because a client does not want
 * forty rows of hostnames and the account manager does not want to scroll
 * past them in a meeting.
 */
export function AgentAttentionList({ agents, limit, total }: { agents: Agent[]; limit?: number; total?: number }) {
  const shown = limit ? agents.slice(0, limit) : agents;
  const more = (total ?? agents.length) - shown.length;
  if (shown.length === 0) {
    return <div className="am-csat-empty">Every agent is checking in, patched and protected.</div>;
  }
  return (
    <>
      <ul className="am-ah-list">
        {shown.map((a) => (
          <li key={a.id}>
            <span className="am-ah-agent">
              <strong>{a.name || `#${a.id}`}</strong>
              <span className="am-ah-sub">
                {a.type || 'Agent'}
                {a.os ? ` · ${a.os}` : ''}
                {a.location ? ` · ${a.location}` : ''}
              </span>
            </span>
            <span className="am-ah-why">{a.flags.join(' · ')}</span>
          </li>
        ))}
      </ul>
      {more > 0 && <p className="am-ah-more">and {num(more)} more</p>}
    </>
  );
}

/** The sentence shown wherever there are no figures to show, and why. */
export function AgentHealthEmpty({ data, error }: { data: AgentHealthSnap | null; error?: string | null }) {
  if (error) return <div className="am-csat-empty">{error}</div>;
  if (data && !data.linked) {
    return (
      <div className="am-csat-empty">
        This client is not linked to a ConnectWise Automate client, so there are no agents to show. An admin can make
        the link under NOC › Estate health › Client mapping.
      </div>
    );
  }
  if (data && data.totals.agents === 0) {
    return <div className="am-csat-empty">Automate has no agents under this client.</div>;
  }
  return <div className="am-csat-empty">Reading the client’s agents from Automate…</div>;
}

/* ------------------------------------------------- the client's own tab -- */

/**
 * The Agent health tab on a client's workspace.
 *
 * Held until Refresh like the Assets and Services tabs beside it: a read is a
 * call to Automate, and these numbers do not need to move while somebody is
 * looking at them.
 */
export function AmAgentHealth({ clientId, clientName }: { clientId: string; clientName: string }) {
  const live = useHeldResource<AgentHealthSnap>(`/am/clients/${clientId}/automate/agent-health`);
  const d = live.data;

  return (
    <section className="card">
      <header className="card-head">
        <div>
          <h2>Agent health</h2>
          <p className="sub">
            {clientName}’s machines, live from ConnectWise Automate.
            {d?.linked && d.totals.agents > 0 && (
              <>
                {' '}Offline past {d.thresholds.offlineDays} days, Windows unpatched past {d.thresholds.patchDays} days,
                virus definitions past {d.thresholds.avDays} days. Patching and antivirus are judged only on agents
                seen in the last {d.thresholds.seenDays} days — a machine that is switched off cannot patch itself.
              </>
            )}
            {live.fetchedAt && (
              <span className="xs subtle">
                {' '}As of{' '}
                {new Date(live.fetchedAt).toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
                .
              </span>
            )}
          </p>
        </div>
        <div className="row wrap">
          <button type="button" className="btn btn-secondary" disabled={live.refreshing} onClick={live.refresh}>
            {live.refreshing ? 'Reading Automate…' : 'Refresh'}
          </button>
        </div>
      </header>

      {live.error && <ErrorBanner message={live.error} />}
      {live.loading && !d && <Loading label="Reading the client’s agents from Automate" />}

      {d && (!d.linked || d.totals.agents === 0) && <AgentHealthEmpty data={d} />}

      {d && d.linked && d.totals.agents > 0 && (
        <>
          <AgentHealthFigures data={d} />
          <h3 className="mt-4">
            Needs attention{' '}
            <span className="xs subtle">
              {d.attentionTotal === 0
                ? 'nothing outstanding'
                : `${num(d.attentionTotal)} of ${num(d.totals.agents)} agents`}
            </span>
          </h3>
          <AgentAttentionList agents={d.attention} total={d.attentionTotal} />
        </>
      )}
    </section>
  );
}

/**
 * The review form's preview of the Agent Health slide — the same figures the
 * slide will show, so the choice to include it is made while looking at what
 * it says rather than at its name.
 */
export function AgentHealthPreview({ clientId }: { clientId: string }) {
  const live = useHeldResource<AgentHealthSnap>(`/am/clients/${clientId}/automate/agent-health`);
  const d = live.data;
  if (live.loading && !d) return <Loading label="Reading the client’s agents from Automate" />;
  if (live.error) return <AgentHealthEmpty data={null} error={live.error} />;
  if (!d || !d.linked || d.totals.agents === 0) return <AgentHealthEmpty data={d} />;
  return (
    <div className="am-csat-preview">
      <AgentHealthFigures data={d} />
      <AgentAttentionList agents={d.attention} limit={4} total={d.attentionTotal} />
    </div>
  );
}
